// frontend/src/ts/protocols/common/components/TauriDragDropZone.tsx
//
// Tauri-only drop zone. Listens to the OS-level drag-drop events
// (requires `dragDropEnabled: true` in tauri.conf.json) and dispatches
// each dropped file's absolute path to `onFilesDropped`. The picker
// fallback (`onPickClicked`) is wired so clicks still open the native
// file dialog.
//
// Renders a clickable zone with visual feedback (border highlight)
// during a hover. Drop fires `onFilesDropped` with the dropped paths.

import React, { useCallback, useEffect, useState } from 'react'
import { Box, Text } from '@mantine/core'
import { IconUpload } from '@tabler/icons-react'
import { getCurrentWebviewWindow } from '@tauri-apps/api/webviewWindow'

interface TauriDragDropZoneProps {
    disabled: boolean
    onFilesDropped: (paths: string[]) => void
    onPickClicked: () => void
}

/**
 * A drop zone that captures OS-level file drops in a Tauri window.
 *
 * Drop-event payloads carry absolute file paths (not browser File
 * objects); the caller converts each path into a queued upload via
 * `store.enqueuePickedFiles` / `store.enqueuePickedFile` -> Rust `enqueue_picked_file`
 * -> durable pending_uploads row -> upload worker. There is no chunk
 * streaming in Tauri mode.
 */
export const TauriDragDropZone: React.FC<TauriDragDropZoneProps> = ({
    disabled, onFilesDropped, onPickClicked,
}) => {
    const [isDragOver, setIsDragOver] = useState(false)

    useEffect(() => {
        // Skip subscription entirely if disabled — avoids registering
        // a listener that no-ops on every event.
        if (disabled) return
        const webview = getCurrentWebviewWindow()
        let unlisten: (() => void) | null = null
        let cancelled = false
        webview.onDragDropEvent((event) => {
            if (cancelled) return
            const type = event.payload.type
            if (type === 'enter' || type === 'over') {
                setIsDragOver(true)
            } else if (type === 'leave') {
                setIsDragOver(false)
            } else if (type === 'drop') {
                setIsDragOver(false)
                const paths = event.payload.paths
                if (paths.length > 0) onFilesDropped(paths)
            }
        }).then((fn) => {
            if (cancelled) fn()
            else unlisten = fn
        }).catch((e) => {
            // Tauri isn't available or the platform doesn't support
            // dragDropEvent; surface to console but don't crash the UI.
            console.error('[TauriDragDropZone] onDragDropEvent failed', e)
        })
        return () => {
            cancelled = true
            if (unlisten) unlisten()
        }
    }, [disabled, onFilesDropped])

    const handleClick = useCallback(() => {
        if (!disabled) onPickClicked()
    }, [disabled, onPickClicked])

    return (
        <Box
            data-testid="tauri-dropzone"
            onClick={handleClick}
            style={{
                border: '1px dashed var(--mantine-color-gray-4)',
                borderRadius: 'var(--mantine-radius-sm)',
                padding: '1rem',
                cursor: disabled ? 'not-allowed' : 'pointer',
                backgroundColor: isDragOver
                    ? 'var(--mantine-color-green-0)'
                    : (disabled ? undefined : undefined),
                borderColor: isDragOver
                    ? 'var(--mantine-color-green-6)'
                    : undefined,
                transition: 'background-color 0.2s ease, border-color 0.2s ease',
            }}
        >
            <Box style={{
                display: 'grid',
                grid: 'auto / auto',
                alignItems: 'center',
                justifyItems: 'center',
                gap: '0.25rem',
                pointerEvents: 'none',
            }}>
                <IconUpload
                    size="1.5em"
                    color={isDragOver
                        ? 'var(--mantine-color-green-6)'
                        : 'var(--mantine-color-dimmed)'}
                />
                <Text size="xs" c={isDragOver ? 'green' : 'dimmed'}>
                    {isDragOver ? 'Drop files here' : 'Add files'}
                </Text>
            </Box>
        </Box>
    )
}
